import { useQuery } from "@tanstack/vue-query"
import { PatientConversationRoleEnum } from "@vitable/asclepius-api-client-ts"
import { useAsclepius } from "@/composables/api/asclepius"
import { useStore } from "@/store"
import { toPrefixedId } from "@/utils/prefixed-id"
import { patientConversationKeys } from "@/modules/patient-conversations/composables/patient-conversations"

const DIRECTORY_STALE_TIME_MS = 5 * 60_000
// Every active staff member fits one page; the pilot gate is applied by the server, not by the picker.
const DIRECTORY_PAGE_SIZE = 1000

/** A staff member as the assignee pickers list them: a `usr_…` id, a name and a display role. */
export interface PatientConversationStaffOption {
  id: string
  fullName: string
  role: PatientConversationRoleEnum
}

/**
 * Active Providers plus Clinical Admins ("Support Team"), sorted by name. Only Clinical Admins may list
 * clinical admins today (`GET /v1/clinical-admins` is 403 for a Provider), so a Provider's picker shows the
 * PROVIDERS section alone.
 */
export function usePatientConversationStaffDirectory() {
  const { getProviderApi, getClinicalAdminApi, callWithResponse } = useAsclepius()
  const store = useStore()

  async function listClinicalAdmins(): Promise<Array<PatientConversationStaffOption>> {
    if (!store.getters["me/isClinicalAdmin"]) return []
    const response = await callWithResponse(() =>
      getClinicalAdminApi().listClinicalAdmins({ page: 1, limit: DIRECTORY_PAGE_SIZE })
    )
    return response.results.map((clinicalAdmin) => ({
      id: toPrefixedId("usr", clinicalAdmin.user_id),
      fullName: clinicalAdmin.full_name,
      role: PatientConversationRoleEnum.SupportTeam
    }))
  }

  async function listStaff(): Promise<Array<PatientConversationStaffOption>> {
    const [providers, clinicalAdmins] = await Promise.all([
      callWithResponse(() => getProviderApi().listProviders({ activeIn: true, page: 1, limit: DIRECTORY_PAGE_SIZE })),
      listClinicalAdmins()
    ])

    const staff: Array<PatientConversationStaffOption> = [
      ...providers.results.map((provider) => ({
        id: toPrefixedId("usr", provider.user_id),
        fullName: provider.full_name,
        role: PatientConversationRoleEnum.Provider
      })),
      ...clinicalAdmins
    ]

    return staff.sort((a, b) => a.fullName.localeCompare(b.fullName))
  }

  return useQuery({
    queryKey: [...patientConversationKeys.all, "staff-directory"],
    queryFn: listStaff,
    staleTime: DIRECTORY_STALE_TIME_MS
  })
}
